import { useCallback, FC, useMemo, useRef } from "react";
import { ItemListItemKind, ItemListItemRowData } from "@/components/item-list/types";
import { ReactMouseEvent } from "@/domains/react/types";
import { observer } from "mobx-react-lite";
import styled from "@emotion/styled";
import { MDSRowItem, MDSRowItemSlotName, MDSTextColor, MDSTextWeight, MDSSpacer } from "@/mds";
import { MDSText, MDSTextVariant } from "@/mds";
import { MDSPanelWidths } from "@/mds/foundations";
import {
  ItemListRowDropdown,
  ItemListTitleIcon,
  ItemListItemSharedByBox,
  ItemListToggleIcon,
} from "@/components/item-list/rows/components";
import { useMatch } from "react-router-dom";
import { appRoutes } from "@/app/router";

const DISABLE_NOTE_PREVIEW_ON_HOVER_ATTR = "data-disable-note-preview-on-hover";
const DISABLE_NOTE_PREVIEW_ON_HOVER_SELECTOR = `[${DISABLE_NOTE_PREVIEW_ON_HOVER_ATTR}="true"]`;

const shouldDisableNotePreviewOnHover = (target: EventTarget | null): boolean =>
  target instanceof Element && !!target.closest(DISABLE_NOTE_PREVIEW_ON_HOVER_SELECTOR);

export interface ItemListItemRowProps {
  data: ItemListItemRowData;
  noSeparator?: boolean;
}

export const ItemListItemRow: FC<ItemListItemRowProps> = observer(
  ({ data: { payload: item, mustClickOnRowTitle = false }, noSeparator }) => {
    const itemPreviewState = useMemo(() => item.createPreviewState?.(), [item]);

    const extraRowContent = useMemo(
      () => item.extraRow && { id: item.extraRow.id, content: item.extraRow.content?.() },
      [item]
    );

    const handleMouseEnter = item.listState?.isHoverable
      ? () => item.listState?.hoverItem(item.id)
      : undefined;

    const inSearchPage = !!useMatch(appRoutes.search({}).path);
    const isHovered = item.id === item.listState?.hoveredItemId;
    const isKeyboardHighlighted = item.id === item.listState?.keyboardHighlightedItemId;
    const isSelected = item.listState?.isSelected(item.id);

    const itemChips = item.chips?.() || null;
    const hasChips = !!itemChips;

    const itemRef = useRef(item);
    itemRef.current = item;

    const handleItemClick = useCallback((event: ReactMouseEvent, canClickItem = true) => {
      event.stopPropagation();
      const item = itemRef.current;
      if (item.listState?.canSelect && event.shiftKey) {
        item.listState.selectItemsFromLastSelectionUntil(item.id);
        return;
      }

      // if (item.listState?.isSelectModeActive || (isMac() ? event.metaKey : event.ctrlKey)) {
      //   item.listState?.toggleItemSelection(item.id);
      //   return;
      // }

      if (canClickItem) {
        item.onClick({ itemId: item.id, event });
      }
    }, []);

    const handleRowClick = (event: ReactMouseEvent) => handleItemClick(event, !mustClickOnRowTitle);

    const handleTitleClick = (event: ReactMouseEvent) => handleItemClick(event);

    const shouldShowInteractiveHover = isHovered;
    const rightAlignedComponent = item.rightAlignedComponent?.(shouldShowInteractiveHover);
    const rightComponentTriggersActions = !!rightAlignedComponent;
    const showActions =
      (!item.listState?.isSelectModeActive || !isSelected) &&
      (rightComponentTriggersActions || (shouldShowInteractiveHover && !!item.dropdown));
    const showChipsAndShare = !rightAlignedComponent || !showActions || inSearchPage;

    const preview =
      itemPreviewState?.previewUi && itemPreviewState?.anchorPreviewAtTheEnd ? (
        <div slot={MDSRowItemSlotName.LastAddon}>{itemPreviewState.previewUi}</div>
      ) : (
        itemPreviewState?.previewUi
      );

    const handleBottomRowMouseEnter = useCallback(
      (event: ReactMouseEvent<HTMLDivElement>) => {
        if (shouldDisableNotePreviewOnHover(event.target)) {
          return;
        }
        itemPreviewState?.handleMouseEnter?.();
      },
      [itemPreviewState]
    );

    const handleBottomRowMouseOver = useCallback(
      (event: ReactMouseEvent<HTMLDivElement>) => {
        if (!shouldDisableNotePreviewOnHover(event.target)) {
          return;
        }

        if (itemPreviewState?.hideImmediately) {
          itemPreviewState.hideImmediately();
        } else {
          itemPreviewState?.handleMouseLeave?.();
        }
      },
      [itemPreviewState]
    );

    const handleBottomRowMouseLeave = useCallback(
      (event: ReactMouseEvent<HTMLDivElement>) => {
        if (shouldDisableNotePreviewOnHover(event.relatedTarget)) {
          return;
        }
        itemPreviewState?.handleMouseLeave?.();
      },
      [itemPreviewState]
    );

    const canSelect = item.listState?.canSelect ?? false;

    const isSearchOrMessage = [ItemListItemKind.Search, ItemListItemKind.Message].includes(
      item.kind
    );

    return (
      <MDSRowItem
        $isSelected={isSelected}
        $isHighlighted={isKeyboardHighlighted}
        $isHovered={isHovered}
        $isSpecial={item.isSpecial}
        $appearance={item.appearance}
        $mustClickOnRowTitle={mustClickOnRowTitle}
        noSeparator={noSeparator}
        className="mds-item-list-row" // Used in e2e tests
        onClick={handleRowClick}
        onMouseEnter={handleMouseEnter}
      >
        <ItemListToggleIcon
          slot={MDSRowItemSlotName.LeftAddon}
          item={item}
          isSelected={isSelected}
          isHighlighted={canSelect && isHovered}
          itemRef={itemRef}
          handleItemClick={handleItemClick}
        />
        <ContentRow>
          <Content>
            <LabelContainer
              onClick={handleTitleClick}
              onMouseEnter={itemPreviewState?.handleMouseEnter}
              onMouseLeave={itemPreviewState?.handleMouseLeave}
            >
              {isSearchOrMessage && (
                <MDSText
                  color={MDSTextColor.Emphasis}
                  variant={MDSTextVariant.TextS}
                  weight={MDSTextWeight.Smedium}
                >
                  {item.label}
                </MDSText>
              )}
              {!isSearchOrMessage && (
                <MDSText variant={MDSTextVariant.TextM} weight={MDSTextWeight.Medium}>
                  {item.label}
                </MDSText>
              )}
              {showChipsAndShare && !!item.titleIcon && (
                <ItemListTitleIcon titleIcon={item.titleIcon} />
              )}
              {showChipsAndShare && (
                <ItemChipsContainerExpanded>{itemChips}</ItemChipsContainerExpanded>
              )}
            </LabelContainer>
          </Content>

          {!showActions && (
            <SharedByAndDateContainer
              $expandWidth={showChipsAndShare && !!item.sharedBy && !!item.dateLabel}
            >
              {showChipsAndShare && !!item.sharedBy && (
                <ItemListItemSharedByBox
                  sharedBy={item.sharedBy}
                  sharedByVariant={item.sharedByVariant}
                />
              )}
              {!!item.dateLabel && (
                <StyledText variant={MDSTextVariant.TextXs} color={MDSTextColor.Secondary}>
                  {item.dateLabel}
                </StyledText>
              )}
            </SharedByAndDateContainer>
          )}
        </ContentRow>

        {extraRowContent && (
          <BottomRow
            slot={MDSRowItemSlotName.BottomAddon}
            key={extraRowContent.id}
            onMouseEnter={handleBottomRowMouseEnter}
            onMouseOver={handleBottomRowMouseOver}
            onMouseLeave={handleBottomRowMouseLeave}
          >
            {!!hasChips && <ItemChipsContainerCollapsed>{itemChips}</ItemChipsContainerCollapsed>}
            <BottomRowContainer $limitWidth={inSearchPage && !showActions}>
              {extraRowContent.content}
            </BottomRowContainer>
          </BottomRow>
        )}

        {preview}

        {showActions && (
          <RightSide
            $expandWidth={!!rightAlignedComponent && !!item.dropdown}
            $topAlign={inSearchPage}
            slot={MDSRowItemSlotName.RightAddon}
          >
            {rightAlignedComponent}
            {item.dropdown && (
              <>
                <MDSSpacer />
                <ItemListRowDropdown itemId={item.id} dropdown={item.dropdown} />
              </>
            )}
          </RightSide>
        )}
      </MDSRowItem>
    );
  }
);

const ContentRow = styled.div`
  display: flex;
  flex: 1;
  overflow: hidden;
  flex-shrink: 1;
`;

const BottomRow = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
  color: ${({ theme }) => theme.colors.text.secondary};
  gap: ${({ theme }) => theme.spacing["2"]};
`;

const Content = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: center;
`;

const LabelContainer = styled.div`
  overflow: hidden;
  cursor: pointer;
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const RightSide = styled.div<{ $expandWidth?: boolean; $topAlign?: boolean }>`
  align-items: flex-end;
  display: flex;
  margin-left: 10px;
  width: ${({ $expandWidth }) => ($expandWidth ? "auto" : "60px")};
  min-width: 60px;
  ${({ $topAlign }) =>
    $topAlign &&
    `
      align-self: flex-start;
      /* To center align to the row title */
      margin-top: -6px;
      /* Stable width so attachments are stable when hovering over the row */
      width: 140px;
  `}
`;

const StyledText = styled(MDSText)`
  justify-self: flex-end;
  text-align: right;
  line-height: 20px;
  flex-shrink: 1;
`;

const SharedByAndDateContainer = styled.div<{ $expandWidth?: boolean }>`
  display: flex;
  flex-direction: row;

  align-items: flex-end;
  gap: 16px;

  min-width: 0;
  margin-left: 10px;

  justify-content: ${({ $expandWidth }) => ($expandWidth ? "space-between" : "flex-end")};
  flex-shrink: ${({ $expandWidth }) => ($expandWidth ? 1 : 0)};
  width: ${({ $expandWidth }) => ($expandWidth ? "200px" : "auto")};

  @container mds-row-item (max-width: ${MDSPanelWidths.headerBreakpoint}px) {
    width: min-content;
  }
`;

const ItemChipsContainerExpanded = styled.div`
  @container mds-row-item (max-width: ${MDSPanelWidths.narrow}px) {
    display: none;
  }
`;

const BottomRowContainer = styled.div<{ $limitWidth?: boolean }>`
  ${({ $limitWidth }) =>
    $limitWidth &&
    `
    /* Stable width so attachments are stable when hovering over the row */
    max-width: calc(100% - 150px);
`}
`;

const ItemChipsContainerCollapsed = styled.div`
  @container mds-row-item (min-width: ${MDSPanelWidths.narrow}px) {
    display: none;
  }
`;
